import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaFileMedical, FaFlask, FaHistory, FaShieldAlt } from "react-icons/fa";
import { supabase } from "../supabaseClient";

function formatDateTime(value) {
  if (!value) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function PatientClinicalHistory({ session, account }) {
  const [documents, setDocuments] = useState([]);
  const [discharges, setDischarges] = useState([]);
  const [tests, setTests] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [consent, setConsent] = useState(null);
  const [consentDraft, setConsentDraft] = useState({
    preferred_name: "",
    emergency_contact_name: "",
    emergency_contact_relationship: "",
    emergency_contact_phone: "",
    communication_method: "",
    interpreter_required: false,
    student_observation_consent: false,
    photography_consent: false,
    information_sharing_consent: false,
    notes: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(async () => {
    if (!session?.user?.id) return;
    setLoading(true);
    setFeedback(null);

    const [documentResult, dischargeResult, testResult, complaintResult, consentResult] = await Promise.all([
      supabase.from("clinical_documents").select("id, document_type, title, content, created_at").eq("portal_account_id", session.user.id).eq("patient_visible", true).order("created_at", { ascending: false }),
      supabase.from("discharge_summaries").select("id, diagnosis, treatment, discharge_advice, follow_up, medications, discharged_at").eq("portal_account_id", session.user.id).order("discharged_at", { ascending: false }),
      supabase.from("diagnostic_requests").select("id, test_type, result_summary, result_flag, resulted_at, reviewed_at").eq("portal_account_id", session.user.id).eq("patient_visible", true).order("requested_at", { ascending: false }),
      supabase.from("complaints").select("id, complained_about, complaint_text, submitted_at, status, management_response, updated_at").eq("portal_account_id", session.user.id).order("submitted_at", { ascending: false }),
      account?.id ? supabase.from("patient_consents").select("*").eq("portal_account_id", account.id).maybeSingle() : Promise.resolve({ data: null, error: null }),
    ]);

    const firstError = [documentResult, dischargeResult, testResult, complaintResult, consentResult].find((r) => r.error)?.error;
    if (firstError) {
      console.error("Unable to load patient clinical history:", firstError);
      setFeedback({ type: "error", text: "Some patient history features are not available yet. Make sure ADD_HOSPITAL_OPERATIONS_SUITE.sql has been run." });
    }

    setDocuments(documentResult.data || []);
    setDischarges(dischargeResult.data || []);
    setTests(testResult.data || []);
    setComplaints(complaintResult.data || []);
    const nextConsent = consentResult.data || null;
    setConsent(nextConsent);
    setConsentDraft({
      preferred_name: nextConsent?.preferred_name || account?.roleplay_name || "",
      emergency_contact_name: nextConsent?.emergency_contact_name || "",
      emergency_contact_relationship: nextConsent?.emergency_contact_relationship || "",
      emergency_contact_phone: nextConsent?.emergency_contact_phone || "",
      communication_method: nextConsent?.communication_method || account?.communication_preferences || "",
      interpreter_required: nextConsent?.interpreter_required === true,
      student_observation_consent: nextConsent?.student_observation_consent === true,
      photography_consent: nextConsent?.photography_consent === true,
      information_sharing_consent: nextConsent?.information_sharing_consent === true,
      notes: nextConsent?.notes || "",
    });
    setLoading(false);
  }, [account?.communication_preferences, account?.id, account?.roleplay_name, session?.user?.id]);

  useEffect(() => { void load(); }, [load]);

  async function saveConsent(event) {
    event.preventDefault();
    if (!account?.id) return;
    setSaving(true);
    setFeedback(null);

    const { data: linkedPatient } = await supabase.from("patients").select("id").eq("portal_account_id", account.id).eq("archived", false).order("arrival_time", { ascending: false }).limit(1).maybeSingle();
    if (!linkedPatient?.id) {
      setSaving(false);
      setFeedback({ type: "error", text: "Consent details can be saved once your portal account has a linked hospital attendance." });
      return;
    }

    const { error } = await supabase.from("patient_consents").upsert({
      patient_id: linkedPatient.id,
      portal_account_id: account.id,
      ...consentDraft,
      updated_by: session?.user?.id || null,
      updated_at: new Date().toISOString(),
    }, { onConflict: "patient_id" });

    setSaving(false);
    if (error) setFeedback({ type: "error", text: error.message || "Your consent details could not be saved." });
    else { setFeedback({ type: "success", text: "Your consent and emergency contact preferences have been saved." }); await load(); }
  }

  const timeline = useMemo(() => {
    const items = [];
    documents.forEach((row) => items.push({ id: `doc-${row.id}`, at: row.created_at, type: row.document_type, title: row.title }));
    discharges.forEach((row) => items.push({ id: `dis-${row.id}`, at: row.discharged_at, type: "Visit summary", title: row.diagnosis }));
    tests.forEach((row) => items.push({ id: `test-${row.id}`, at: row.reviewed_at || row.resulted_at, type: "Result", title: `${row.test_type}${row.result_flag ? ` · ${row.result_flag}` : ""}` }));
    complaints.forEach((row) => items.push({ id: `complaint-${row.id}`, at: row.updated_at || row.submitted_at, type: "Complaint", title: `Complaint · ${row.status || "Submitted"}` }));
    return items.sort((a, b) => new Date(b.at || 0).getTime() - new Date(a.at || 0).getTime());
  }, [complaints, discharges, documents, tests]);

  return (
    <section id="patient-history" className="pguh-public-content-card pguh-patient-history">
      <div className="pguh-public-section-card__header">
        <div><span className="pguh-public-kicker">Your record</span><h2>Medical history & documents</h2><p>Patient-safe roleplay information shared with you by RUHT.</p></div>
        <FaHistory aria-hidden="true" />
      </div>
      {feedback && <div className={`pguh-timesheet-message pguh-timesheet-message--${feedback.type}`}>{feedback.text}</div>}
      {loading ? <p>Loading your history…</p> : (
        <>
          <div className="pguh-patient-history__summary"><div><span>Documents</span><strong>{documents.length}</strong></div><div><span>Visit summaries</span><strong>{discharges.length}</strong></div><div><span>Shared results</span><strong>{tests.length}</strong></div><div><span>Complaints</span><strong>{complaints.length}</strong></div></div>

          <div className="pguh-patient-history__grid">
            <div className="pguh-advanced-list">
              <h3 className="nhsuk-heading-s"><FaHistory aria-hidden="true" /> Recent history</h3>
              {timeline.length === 0 ? <p>No patient-visible history has been shared yet.</p> : timeline.slice(0, 12).map((row) => <article key={row.id}><div><strong>{row.title}</strong><span>{row.type}</span></div><small>{formatDateTime(row.at)}</small></article>)}
            </div>

            <div className="pguh-advanced-list">
              <h3 className="nhsuk-heading-s"><FaFileMedical aria-hidden="true" /> Documents & visit summaries</h3>
              {documents.map((row) => <details key={row.id}><summary><strong>{row.title}</strong> · {row.document_type} · {formatDateTime(row.created_at)}</summary><div className="pguh-document-body">{row.content}</div></details>)}
              {discharges.map((row) => <details key={row.id}><summary><strong>{row.diagnosis}</strong> · Discharge · {formatDateTime(row.discharged_at)}</summary><div className="pguh-document-body">{row.treatment && <p><strong>Treatment:</strong> {row.treatment}</p>}{row.medications && <p><strong>Medication:</strong> {row.medications}</p>}{row.discharge_advice && <p><strong>Advice:</strong> {row.discharge_advice}</p>}{row.follow_up && <p><strong>Follow-up:</strong> {row.follow_up}</p>}</div></details>)}
            </div>

            <div className="pguh-advanced-list">
              <h3 className="nhsuk-heading-s"><FaFlask aria-hidden="true" /> Shared test results</h3>
              {tests.length === 0 ? <p>No test results have been shared with your portal.</p> : tests.map((row) => <article key={row.id}><div><strong>{row.test_type}</strong><span>{row.result_flag || "Result"}</span></div><p>{row.result_summary || "A result has been recorded."}</p><small>{formatDateTime(row.reviewed_at || row.resulted_at)}</small></article>)}
            </div>

            <div className="pguh-advanced-list">
              <h3 className="nhsuk-heading-s">Complaint tracking</h3>
              {complaints.length === 0 ? <p>You have no tracked complaints.</p> : complaints.map((row) => <article key={row.id}><div><strong>{row.complained_about || "RUHT service"}</strong><span>{row.status || "Submitted"}</span></div><p>{row.complaint_text}</p>{row.management_response && <div className="pguh-public-info-strip"><strong>Management response:</strong> {row.management_response}</div>}<small>Submitted {formatDateTime(row.submitted_at)}</small></article>)}
            </div>
          </div>

          <form className="pguh-clinical-form pguh-patient-consent" onSubmit={saveConsent}>
            <h3 className="nhsuk-heading-m"><FaShieldAlt aria-hidden="true" /> Consent & emergency contact</h3>
            <p>These roleplay preferences are visible to staff when you have a linked hospital attendance.</p>
            <div className="pguh-form-grid"><label>Preferred name<input className="nhsuk-input" value={consentDraft.preferred_name} onChange={(e) => setConsentDraft((d) => ({ ...d, preferred_name: e.target.value }))} /></label><label>Communication method<input className="nhsuk-input" value={consentDraft.communication_method} onChange={(e) => setConsentDraft((d) => ({ ...d, communication_method: e.target.value }))} /></label><label>Emergency contact<input className="nhsuk-input" value={consentDraft.emergency_contact_name} onChange={(e) => setConsentDraft((d) => ({ ...d, emergency_contact_name: e.target.value }))} /></label><label>Relationship<input className="nhsuk-input" value={consentDraft.emergency_contact_relationship} onChange={(e) => setConsentDraft((d) => ({ ...d, emergency_contact_relationship: e.target.value }))} /></label><label>Contact number<input className="nhsuk-input" value={consentDraft.emergency_contact_phone} onChange={(e) => setConsentDraft((d) => ({ ...d, emergency_contact_phone: e.target.value }))} /></label></div>
            <div className="pguh-consent-checks"><label><input type="checkbox" checked={consentDraft.interpreter_required} onChange={(e) => setConsentDraft((d) => ({ ...d, interpreter_required: e.target.checked }))} /> Interpreter required</label><label><input type="checkbox" checked={consentDraft.student_observation_consent} onChange={(e) => setConsentDraft((d) => ({ ...d, student_observation_consent: e.target.checked }))} /> Student observation consent</label><label><input type="checkbox" checked={consentDraft.photography_consent} onChange={(e) => setConsentDraft((d) => ({ ...d, photography_consent: e.target.checked }))} /> Roleplay photography consent</label><label><input type="checkbox" checked={consentDraft.information_sharing_consent} onChange={(e) => setConsentDraft((d) => ({ ...d, information_sharing_consent: e.target.checked }))} /> Roleplay information-sharing consent</label></div>
            <label className="nhsuk-label">Other notes</label><textarea className="nhsuk-textarea" rows="3" value={consentDraft.notes} onChange={(e) => setConsentDraft((d) => ({ ...d, notes: e.target.value }))} />
            <button className="nhsuk-button" disabled={saving}>{saving ? "Saving…" : "Save consent & contact details"}</button>
          </form>
        </>
      )}
    </section>
  );
}
